import { bonusStatuses, bonusTypes, DEFAULT_LANG } from 'src/common/constants';

const { DEPOSIT, RELOAD_DEPOSIT } = bonusTypes;
const { ACTIVATED } = bonusStatuses;

export const checkDepositBonusTypeFits = bonusType => [DEPOSIT, RELOAD_DEPOSIT].includes(bonusType);

export const getBonusActivationSortValue = bonus => bonus?.createTime ?? bonus?.id ?? 0;

export const normalizeBonusWithFinanceData = (bonus, currency) => {
  const normalizedBonus = bonus?.bonus ?? bonus;
  const financeData =
    normalizedBonus?.financeDataList?.find(item => item.currency === currency) ??
    bonus?.financeData;

  return {
    ...bonus,
    ...normalizedBonus,
    financeData,
    id: bonus?.id ?? normalizedBonus?.id,
  };
};

export const getBonusMinDeposit = (bonus, currency) =>
  bonus?.financeData?.minDeposit ??
  bonus?.financeDataList?.find(item => item.currency === currency)?.minDeposit ??
  0;

export const getLocalizedBonusInfo = (bonus, language) => {
  const normalizedBonus = bonus?.bonus ?? bonus;
  const { localizedInfoList = [], name, nameHighlighted } = normalizedBonus ?? {};
  const currentLanguage = language?.toLowerCase();
  const localizedInfo =
    localizedInfoList.find(info => info.language?.toLowerCase() === currentLanguage) ??
    localizedInfoList[0];

  return {
    name: localizedInfo?.name ?? name,
    nameHighlighted: localizedInfo?.nameHighlighted ?? nameHighlighted,
  };
};

const getBonusActivationToastId = data => {
  const bonusId = data?.bonusId ?? data?.bonus?.id ?? data?.id;

  return bonusId != null ? `bonus-activation-success-${bonusId}` : null;
};

export const getBonusActivationToastOptions = (data, language = DEFAULT_LANG) => {
  const { bonus, bonusName, bonusNameHighlighted, name, nameHighlighted } = data ?? {};
  const localizedInfo = getLocalizedBonusInfo(data, language);
  const id = getBonusActivationToastId(data);
  const options = {
    data: {
      bonusName: bonusName ?? name ?? localizedInfo.name ?? bonus ?? null,
      bonusNameHighlighted:
        bonusNameHighlighted ?? nameHighlighted ?? localizedInfo.nameHighlighted ?? null,
    },
    type: 'bonusActivationSuccess',
  };

  return id ? { ...options, id } : options;
};

export const pickBonusWithLowestMinDeposit = (bonuses, currency) => {
  if (!bonuses?.length) {
    return null;
  }

  let best = null;
  let bestMin = Infinity;

  for (const item of bonuses) {
    const minDeposit = getBonusMinDeposit(item, currency);
    if (minDeposit > 0 && minDeposit < bestMin) {
      bestMin = minDeposit;
      best = item;
    }
  }

  return best ?? bonuses[0];
};

export const findLatestActivatedBonus = ({ activeBonuses, isMatchingBonus }) =>
  activeBonuses
    .filter(({ bonus, status }) => status === ACTIVATED && isMatchingBonus(bonus))
    .reduce((latestBonus, currentBonus) => {
      if (!latestBonus) {
        return currentBonus;
      }

      return getBonusActivationSortValue(currentBonus) >= getBonusActivationSortValue(latestBonus)
        ? currentBonus
        : latestBonus;
    }, null);

export const findTransportMatchedBonus = ({
  activeBonuses,
  bonusId,
  bonusInstanceId,
  bonuses,
  currency,
  isMatchingBonus,
}) => {
  const matchedActiveBonus = activeBonuses.find(({ bonus, id }) => {
    return isMatchingBonus(bonus) && (id === bonusInstanceId || bonus?.id === bonusId);
  });

  if (matchedActiveBonus) {
    return normalizeBonusWithFinanceData(matchedActiveBonus, currency);
  }

  const matchedAvailableBonus = bonuses.find(bonus => {
    return isMatchingBonus(bonus) && bonus.id === bonusId;
  });

  if (!matchedAvailableBonus) {
    return null;
  }

  return normalizeBonusWithFinanceData(matchedAvailableBonus, currency);
};

export const normalizeDepositAmounts = depositAmounts => {
  if (!depositAmounts?.length) return [];

  if (typeof depositAmounts[0] !== 'object') {
    return depositAmounts.map(depositAmount => ({
      isDefault: false,
      value: depositAmount,
    }));
  }

  return depositAmounts;
};
